import { 
  Divider, Grid, Box, 
  Typography, Paper, Link, 
  RadioGroup, FormControlLabel, Radio, 
  FormHelperText, FormLabel, TextField,
  Checkbox, FormControl,
} from "@mui/material"
import { RichContent } from "@gisce/oficina-virtual-components";
import { FC, useContext, useEffect, useState } from "react";
import { useForm, Controller, FormProvider } from "react-hook-form"
import { z, ZodErrorMap, ZodIssueCode } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from "react-redux";
import { default as cupsService } from "../services/cups";
import {
  validarReferenciaCatastral,
} from "../../form-fields/AddressForm/AddressField";
import CNAEField from "../../form-fields/CNAEField";
import { cupsValid } from "../../form-fields/CUPSField";
import ComerOriginSelectField from "../../form-fields/ComerOriginSelectField";
import FileUploadField, {
  validateFileUpload,
} from "../../form-fields/FileUploadField";
import ResidenceTypeSelect from "../../form-fields/ResidenceTypeSelect";
import { useTranslation } from "react-i18next";
import { IStepForm, IStepInstallationFormData } from "../types/stepsFormData";
import { RootState } from "@/store";
import { useSettingsContext } from "../../Settings/hooks/useSettingsContext";
import { Address } from "@/components/form-fields/AddressForm/types/address";
import AddressForm from "@/components/form-fields/AddressForm/AddressForm";
import { SubmitButtonContext } from "../Contractation";

interface FormInstallationProps {
  gas: boolean;
}

const FormInstallation: FC<FormInstallationProps & Pick<IStepForm<IStepInstallationFormData>, "onSubmit" | "type">> = ({
  gas, 
  type,
  onSubmit
}) => {
  const submitButtonContext = useContext(SubmitButtonContext);
  const Settings = useSettingsContext();
  const { t } = useTranslation();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const contractation = useSelector((state: RootState) => state[`${type}Contractation`]);
  const installationStep = useSelector((state: RootState) => state[`${type}Contractation`].steps.installation);
  const [comerOriginEnabled, setComerOriginEnabled] = useState(Settings?.contractation?.selectComerOrigin ?? false);

  const onInstallationSubmit = async (values: IStepInstallationFormData) => {
    await onSubmit(values)
  };

  const handleNoAvailableComersUpdate = () => {
    setComerOriginEnabled(false);
  };

  const customErrorMap: ZodErrorMap = (issue, ctx) => {
    if (issue.code === ZodIssueCode.invalid_enum_value) {
      return { message: t('common:text.required_field') };
    }
    if (issue.code === ZodIssueCode.invalid_type) {
      return { message: t('common:text.required_field') };
    }

    // Retorna el missatge per defecte per altres errors
    return { message: ctx.defaultError };
  };

  // TODO: Check if everything works
  const schema = z.object({

    leadsConsent: z.boolean().refine(
      (val) => {
        return (!Settings?.contractation?.requireLeadConsent) || 
          (Settings?.contractation?.requireLeadConsent && val)
      }, {
        message: t('common:text.required_field')
      }
    ),
    cups: z.string().min(1, t('common:text.required_field'))
    .superRefine((cups, ctx) => {
      if (!cupsValid(cups)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('common:text.contractation_cups_not_valid')
        });
      }
    }),

    address: z.object({
      dp: z.string().min(1, t('common:text.required_field'))
        .min(5, t('common:text.contractation_postal_code_length')),
      ine: z.string().min(1, t('common:text.required_field')),
      state: z.number({errorMap: customErrorMap}).min(1, t('common:text.required_field')),
      poblacio: z.string({errorMap: customErrorMap}).min(1, t('common:text.required_field')),
      tv: z.number({errorMap: customErrorMap}).min(1, t('common:text.required_field')),
      nv: z.string().min(1, t('common:text.required_field')).max(256, 
        t('common:text.address_validation_nv_length')),
      pnp: z.string().min(1, t('common:text.required_field')).max(9, 
        t('common:text.address_validation_pnp_length')),
      bq: z.string().max(4, t('common:text.address_validation_bq_length')).optional(),
      es: z.string().max(4, t('common:text.address_validation_es_length')).optional(),
      pt: z.string().max(10, t('common:text.address_validation_pt_length')).optional(),
      pu: z.string().max(4, t('common:text.address_validation_pu_length')).optional(),
      ref_catastral: z.string()
        .refine(
          val => validarReferenciaCatastral(val), 
          t('common:text.contractation_not_valid_ref')
        ).optional(),
      aclarador: z.string().optional()
    }) satisfies z.ZodType<Address>,

    cupsSIPSData: z.object({
      accessTariff: z.number().nullable(),
      cupsAvailable: z.boolean().refine(val => val, {
        message: t('common:text.contractation_cups_not_available')}),
      power: z.number().nullable(),
      power2: z.number().nullable(),
      power3: z.number().optional(),
      power4: z.number().optional(),
      power5: z.number().optional(),
      power6: z.number().optional(),
      selfcons: z.boolean().optional(),
      selfconsCode: z.string().nullable().optional(),
      sipsDataAvailable: z.boolean().optional()
    }),

    residenceType: z.enum(["habitual", "second", "business"], {errorMap: customErrorMap}),
    cnae: z.object({
      descripcio: z.string().min(1, t('common:text.required_field')),
      id: z.number().min(1, t('common:text.required_field')),
      name: z.string().min(1, t('common:text.required_field')),
    }, {errorMap: customErrorMap}) satisfies z.ZodType<IStepInstallationFormData["cnae"]>,
    
    activeContract: z.boolean({errorMap: customErrorMap}),
    comerOrigin: z.number().nullable().refine(val => {
      if (comerOriginEnabled) {
        return val
      } else {
        return true
      }
    }, {message: t('common:text.required_field')}),

    acceptMorosityVerification: z.boolean().refine(val => {
      if (Settings?.contractation?.mustAcceptMorosityVerification) {
        return val
      } else {
        return true
      }
    }, {message: t('common:text.required_field')}),

    invoice: z.object({
      name: z.string(),
      content: z.string().regex(/^data:.*\/[a-zA-Z]+;base64,/, t('common:text.invalid_attachment_type')),
    })
      .array()
    }).superRefine((data, ctx) => {
    if (data.residenceType === "business" && !data.cnae?.id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('common:text.required_field'),
        path: ["cnae"],
      });
    }
    if ( 
      data.activeContract === true && 
      (Settings?.contractation?.invoiceRequired ?? true) &&
      ( !data.invoice || data.invoice?.length === 0 )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('common:text.required_field'),
        path: ["invoice"],
      });
    }
  }) satisfies z.ZodType<Omit<IStepInstallationFormData, "address" | "cupsSIPSData">>;

  const methods = useForm<IStepInstallationFormData>({
    shouldUnregister: false,
    resolver: zodResolver(schema),
    defaultValues: {
      leadsConsent: contractation.leadsConsent,
      ...installationStep
    }
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    register,
    formState: { errors, isSubmitting }
  } = methods;

  const watchedResidenceType = watch("residenceType");
  const watchedActiveContract = watch("activeContract");

  useEffect(() => {
    submitButtonContext?.setValues({
      onClick: () => handleSubmit(onInstallationSubmit)(),
      loading: isSubmitting
    });
  }, [isSubmitting, handleSubmit]);

  // This is needed because the authentication is async and can finish
  // after rendering the field. Setting only the default value was not 
  // enough and leadConsent was some times false when isAuthenticated was true.
  useEffect(() => {
    setValue("leadsConsent", getValues("leadsConsent") || isAuthenticated);
  }, [isAuthenticated]);

  useEffect(() => {
    if (watchedResidenceType == "habitual" || watchedResidenceType == "second") {
      setValue("cnae", { 
        id: 986, 
        name: "9820", 
        descripcio: "9820: Actividades de los hogares como productores de servicios para uso propio" 
      },)
    }
  }, [watchedResidenceType]);

  const leadsNote = (
    <RichContent
      content={t('common:text.contractation_leads_note', {
        client: Settings?.organization.name ?? "",
        privacyPolicy: Settings?.links?.privacyPolicy ?? ""
      })}
    />
  );

  const cupsMethods = register("cups");

  const handleCUPSChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    cupsMethods.onChange(event);
    if (cupsValid(event.target.value)) {
      if (type == "electricity") {
        cupsService.validateElectricityCUPSAvailability(
          event.target.value
        )
        .then(data => {
          setValue("cupsSIPSData", data);
          if (data.address.zipcode) {
            setValue("address.dp", data.address.zipcode);
          }
        })
      
      } else {
        cupsService.validateGasCUPSAvailability(
          event.target.value
        )
        .then(data => {
          setValue("cupsSIPSData", data);
          if (data.address.zipcode) {
            setValue("address.dp", data.address.zipcode);
          }
        })
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onInstallationSubmit)}>
        <Box sx={{ml: {md: 5}, mr: {md: 5}}}>
          <Grid container spacing={0}>
            <Grid item xs={12}>
              <Typography variant="h6">
                {t('common:text.contractation_cups_title')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label={
                  type == "electricity" ? (
                    gas ? 
                      t('common:text.contractation_cups_electricity') 
                    : 
                      t('common:text.contractation_cups')    
                  )
                  :
                  t('common:text.contractation_cups_gas') 
                }
                {...cupsMethods}
                onChange={handleCUPSChange}
                error={!!(errors.cups) || !!(errors?.cupsSIPSData?.cupsAvailable)}
                style={{ width: "100%" }}
              />
              {!!(errors.cups) &&
                <FormHelperText error={true}>
                  {errors.cups.message}
                </FormHelperText>
              }
              {!!(errors?.cupsSIPSData?.cupsAvailable) &&
                <FormHelperText error={true}>
                  {errors.cupsSIPSData.cupsAvailable.message}
                </FormHelperText>
              }
            </Grid>
            <Grid item xs={12}>
              <div className="helper-text">
                {t(type == "electricity" ? 
                  'common:text.contractation_cups_helper' 
                  : 'common:text.contractation_cups_gas_helper'
                )}
              </div>
            </Grid>
          </Grid>
          <AddressForm
            // showMobile={Settings.contractation?.showMobile} 
            // showEmail={Settings.contractation?.showEmail}
          />
          <Divider sx={{ mb: 3, mt: 3 }} />
          <Grid container spacing={0}>
            <Grid item xs={12} sx={{ paddingTop: 0, paddingBottom: 0 }}>
              <Typography variant="h6">
                {t('common:text.contractation_housing_consumption')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={!(watchedResidenceType === 'business') ? 6 : 12}>
              <Controller
                name={"activeContract"}
                control={control}
                render={({field}) => (
                  <FormControl>
                    <FormLabel>
                      {t(type == "electricity" ? 
                        "common:text.contractation_current_active_contract_question" 
                        : "common:text.contractation_current_active_gas_contract_question"
                      )}
                    </FormLabel>

                    <RadioGroup
                      {...field}
                      onChange={(event) => field.onChange(JSON.parse(event.target.value))}
                      row
                      name="current-active-contract-radio-group"
                    >
                      <FormControlLabel value={true} control={<Radio/>} label={t("common:text.generic_yes")}/>
                      <FormControlLabel value={false} control={<Radio />} label={t("common:text.generic_no")}/>
                    </RadioGroup>
                    { !!(errors.activeContract) &&
                      <FormHelperText error={true}>
                        {errors.activeContract.message}
                      </FormHelperText>
                    }
                  </FormControl>
                )}
              />
              <Grid container spacing={{xs:2, md:3}}>
                <Grid item xs={12} md={!(watchedResidenceType === 'business') ? 12 : 4}>
                  <Controller
                    name={"residenceType"}
                    control={control}
                    render={({ field }) => <ResidenceTypeSelect
                      label={t('common:text.contractation_access_tariff')}
                      meta={{
                        touched: !!errors.residenceType?.message,
                        error: errors.residenceType?.message,
                      }}
                      input={field}
                    />}
                  />
                </Grid>
                {watchedResidenceType === 'business' &&
                  <Grid item xs={12} md={8}>

                    <Controller
                      name={"cnae"}
                      control={control}
                      render={({ field }) => <CNAEField
                        meta={{
                          touched: !!errors.cnae?.message,
                          error: errors.cnae?.message,
                        }}
                        input={field}
                      />}
                    />
                  </Grid>
                }
              </Grid>
            </Grid>
          </Grid>
          {comerOriginEnabled &&
            <Grid item xs={12} md={8} sx={{ mb: 3, mt: 3 }}>
              <Controller
                name={"comerOrigin"}
                control={control}
                render={({ field }) => <ComerOriginSelectField
                  onNoAvailableComersUpdate={handleNoAvailableComersUpdate}
                  gas={type == "gas"}
                  meta={{
                    touched: !!errors.comerOrigin?.message,
                    error: errors.comerOrigin?.message,
                  }}
                  input={field}
                />}
              />
            </Grid>
          }
          {watchedActiveContract === true &&
            <>
              <Divider sx={{ mb: 3, mt: 3 }} />
              <Typography variant="h6">
                {t('common:text.contractation_last_invoice')}
              </Typography>
              <Paper sx={{ padding: 2, mt: 1 }} variant="outlined">
                <Controller
                  name={"invoice"}
                  control={control}
                  render={({ field }) => <FileUploadField
                    min={1}
                    max={3}
                    label={
                      t('common:text.contractation_last_invoice') + 
                      `${Settings?.contractation?.invoiceRequired ? "*" : ""}`
                    }
                    hint={t(type == "electricity" ? 
                      'common:text.contractation_last_invoice_helper' : 
                      'common:text.contractation_last_gas_invoice_helper')}
                    anotherLabel={t('common:text.contractation_last_invoice_add')}
                    removeLabel={t('common:text.remove')}
                    meta={{
                      touched: !!errors.invoice,
                      error: Array.isArray(errors?.invoice) && errors.invoice.map(err => err.content.message),
                    }}
                    input={field}
                  />}
                />
                { !!(errors.invoice) && !Array.isArray(errors.invoice) &&
                  <FormHelperText error={true} >
                    {errors.invoice.message}
                  </FormHelperText>
                }
              </Paper>
            </>
          }
          {
            Settings?.contractation?.mustAcceptMorosityVerification && (
              <Box mt={5}>
                <Controller
                  name={"acceptMorosityVerification"}
                  control={control}
                  render={({field}) => (
                    <FormControl>
                      <FormLabel>
                        {t("common:text.contractation_must_grant_morosity_verification_p1")}
                      </FormLabel>

                      <RadioGroup
                        {...field}
                        onChange={(e) => {
                          field.onChange(e.target.value === "true" ? true : false)
                        }}
                        name="authorization-radio-group"
                        sx={{mb: 1}}
                      >
                        <FormControlLabel value={true} control={<Radio/>} label={t("common:text.contractation_authorize_grant_morosity_verification")}/>
                        <FormControlLabel value={false} control={<Radio />} label={t("common:text.contractation_unauthorize_grant_morosity_verification")}/>
                      </RadioGroup>
                      { !!(errors.acceptMorosityVerification) &&
                        <FormHelperText error={true} >
                          {errors.acceptMorosityVerification.message}
                        </FormHelperText>
                      }
                      <FormLabel>
                        { t("common:text.contractation_must_grant_morosity_verification_p2") }
                        {
                          t("common:text.contractation_must_grant_morosity_verification_link") && (
                            <Link 
                              color="primary" 
                              href={t("common:text.contractation_must_grant_morosity_verification_link")} 
                              target="_blank"
                            >
                              { t("common:text.contractation_must_grant_morosity_verification_link_text") }
                            </Link>
                          )
                        }
                        
                      </FormLabel>
                    </FormControl>
                  )}
                />
              </Box>
            )
          }

        </Box>
        {Settings?.features?.leads && (
          // TODO: Remove className and add Typography
          <Box className="helper-text" sx={{ mb: 3, mt: 3 }}>
            {isAuthenticated ? (
              leadsNote
            ) : (
              <>
                <Controller
                  name={"leadsConsent"}
                  control={control}
                  render={({ field }) => <FormControlLabel
                    control={<Checkbox
                      {...field}
                      checked={field.value}
                      />} 
                      label={leadsNote}
                  />}
                />
                {errors.leadsConsent?.message &&
                  <FormHelperText error={true}>
                    {errors.leadsConsent.message}
                  </FormHelperText>
                }
              </>
            )}
          </Box>
        )}
      </form>
    </FormProvider>
  );
};

export default FormInstallation;